import { collection, doc, getDoc, getDocs, query, orderBy, where, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { prefetch } from './prefetchCache';
import { fetchTagMappings } from '../components/practice/TagManagementPanel';
import { fetchTitleMappings } from '../components/practice/TitleManagementPanel';
import { getUserRole } from './userManagement';

export const PREFETCH_KEYS = {
  sessionHistory: (uid) => `sessionHistory:${uid}`,
  practiceDays: (uid) => `practiceDays:${uid}`,
  completedDays: (uid) => `completedDays:${uid}`,
  performances: (uid) => `performances:${uid}`,
  journalTemplates: (uid) => `journalTemplates:${uid}`,
  practicePlans: (uid) => `practicePlans:${uid}`,
  tagMappings: (uid) => `tagMappings:${uid}`,
  titleMappings: (uid) => `titleMappings:${uid}`,
  userRole: (uid) => `userRole:${uid}`,
  practiceTokens: (uid) => `practiceTokens:${uid}`,
  tokenMarkedDays: (uid) => `tokenMarkedDays:${uid}`,
  celebratedStreaks: (uid) => `celebratedStreaks:${uid}`,
  instructorRelationships: (uid) => `instructorRelationships:${uid}`,
  studentRelationships: (uid) => `studentRelationships:${uid}`,
  studentGroups: (uid) => `studentGroups:${uid}`,
};

export function startPrefetch(uid) {
  console.log("🚀 Starting background data prefetch for user:", uid);

  prefetch(PREFETCH_KEYS.sessionHistory(uid), () =>
    getDocs(query(
      collection(db, "users", uid, "sessionHistory"),
      orderBy("date", "desc"),
      limit(30)
    ))
  );

  // NOTE: the full session set (formerly a limit(2000) prefetch — the dominant
  // login read cost for power users) is now served from the IndexedDB mirror +
  // delta sync (see utils/sessionSync.js), so it is no longer prefetched here.
  // The limit(30) sessionHistory prefetch above stays as a fast path for the
  // paginated history list.

  prefetch(PREFETCH_KEYS.practiceDays(uid), () =>
    getDocs(query(
      collection(db, "users", uid, "practiceDays"),
      orderBy("dayKey", "desc"),
      limit(2000)
    ))
  );

  prefetch(PREFETCH_KEYS.completedDays(uid), () =>
    getDoc(doc(db, "users", uid, "settings", "completedDays"))
  );

  prefetch(PREFETCH_KEYS.performances(uid), () =>
    getDocs(query(
      collection(db, "users", uid, "performances"),
      orderBy("date", "desc")
    ))
  );

  prefetch(PREFETCH_KEYS.journalTemplates(uid), () =>
    getDocs(query(
      collection(db, "users", uid, "performanceJournalTemplates"),
      orderBy("name")
    ))
  );

  prefetch(PREFETCH_KEYS.practicePlans(uid), () =>
    getDocs(collection(db, "users", uid, "practicePlans"))
  );

  prefetch(PREFETCH_KEYS.tagMappings(uid), () =>
    fetchTagMappings(uid)
  );

  prefetch(PREFETCH_KEYS.titleMappings(uid), () =>
    fetchTitleMappings(uid)
  );

  prefetch(PREFETCH_KEYS.userRole(uid), () =>
    getUserRole(uid)
  );

  prefetch(PREFETCH_KEYS.practiceTokens(uid), () =>
    getDoc(doc(db, "users", uid, "settings", "practiceTokens"))
  );

  prefetch(PREFETCH_KEYS.tokenMarkedDays(uid), () =>
    getDoc(doc(db, "users", uid, "settings", "tokenMarkedDays"))
  );

  prefetch(PREFETCH_KEYS.celebratedStreaks(uid), () =>
    getDoc(doc(db, "users", uid, "settings", "celebratedStreaks"))
  );

  prefetch(PREFETCH_KEYS.instructorRelationships(uid), () =>
    getDocs(query(
      collection(db, "instructorStudentRelationships"),
      where("instructorId", "==", uid),
      orderBy("lastInteractionDate", "desc"),
      limit(100)
    ))
  );

  prefetch(PREFETCH_KEYS.studentRelationships(uid), () =>
    getDocs(query(
      collection(db, "instructorStudentRelationships"),
      where("studentId", "==", uid),
      orderBy("lastInteractionDate", "desc"),
      limit(100)
    ))
  );

  prefetch(PREFETCH_KEYS.studentGroups(uid), () =>
    getDocs(query(
      collection(db, "studentGroups"),
      where("instructorId", "==", uid),
      orderBy("name")
    ))
  );
}
